import { useCallback } from "react";
import { DataTable, type DataTableColumn } from "@shop-ledger/ui-components";
import { usePlatformClient } from "../../api/ClientContext";
import { MockBadge } from "../../components/MockBadge";
import { StatusBanner } from "../../components/StatusBanner";
import { useAsyncData } from "../../lib/useAsyncData";
import { annotateQueueAge, countAgedBeyondThreshold, formatAge, DEFAULT_STALE_THRESHOLD_MS } from "../../domain/queueAge";
import type {
  PaymentReconciliationQueueItem,
  StorageQuotaWarning,
  SyncConflictQueueItem,
} from "../../api/types";

interface QueuesData {
  conflicts: SyncConflictQueueItem[];
  reconciliations: PaymentReconciliationQueueItem[];
  storageWarnings: StorageQuotaWarning[];
}

function AgeBadge({ ms, aged }: { ms: number; aged: boolean }) {
  return <span className={`badge ${aged ? "badge--aged" : "badge--fresh"}`}>{formatAge(ms)}</span>;
}

/**
 * §9.5's platform-wide operational queues — deliberately NOT per-tenant, so an operator
 * sees backlog across every customer at once, per the spec's explicit framing. Every
 * queue's "age" column and the stale-count stat tiles are computed by
 * `src/domain/queueAge.ts`'s real threshold math (default 15 minutes) run against each
 * item's `created_at` timestamp — genuine arithmetic either way, unit-tested
 * (`domain/queueAge.test.ts`), not a static "aged" label baked into a fixture.
 *
 * Real vs. mocked, by client: with `realPlatformApiClient` (the default), the sync-conflicts
 * and payment-pending queues are genuine `GET /platform/queues/*` reads — real backlog across
 * every tenant in Postgres. The storage-quota-warnings panel stays permanently mock-badged
 * regardless of which client is active: `Tenant` gained plan/quota columns in the real backend
 * (2026-07-13), but no platform route reads or aggregates them yet, so there is nothing to
 * query — `realPlatformApiClient.listStorageQuotaWarnings` always returns `[]`, and this page
 * never lets that empty array be misread as "no tenant is near quota" (a reassuring, real fact)
 * versus "this signal doesn't exist" (the actual fact).
 */
export function QueuesPage() {
  const client = usePlatformClient();

  const fetchAll = useCallback(async (): Promise<QueuesData> => {
    const [conflicts, reconciliations, storageWarnings] = await Promise.all([
      client.listSyncConflicts(),
      client.listPendingReconciliations(),
      client.listStorageQuotaWarnings(),
    ]);
    return { conflicts, reconciliations, storageWarnings };
  }, [client]);

  const { data, loading, error } = useAsyncData(fetchAll, [fetchAll]);

  const agedConflicts = annotateQueueAge(data?.conflicts ?? []);
  const agedReconciliations = annotateQueueAge(data?.reconciliations ?? []);

  const conflictColumns: DataTableColumn<(typeof agedConflicts)[number], string>[] = [
    { key: "tenant", header: "Tenant", render: (row) => row.item.tenant_name },
    { key: "entity_type", header: "Entity type", render: (row) => row.item.entity_type },
    { key: "status", header: "Status", render: (row) => row.item.status },
    { key: "age", header: "Age", align: "right", render: (row) => <AgeBadge ms={row.ageMs} aged={row.agedBeyondThreshold} /> },
  ];

  const reconciliationColumns: DataTableColumn<(typeof agedReconciliations)[number], string>[] = [
    { key: "tenant", header: "Tenant", render: (row) => row.item.tenant_name },
    { key: "amount", header: "Amount", align: "right", render: (row) => row.item.amount },
    { key: "status", header: "Status", render: (row) => row.item.status },
    { key: "age", header: "Age", align: "right", render: (row) => <AgeBadge ms={row.ageMs} aged={row.agedBeyondThreshold} /> },
  ];

  const storageColumns: DataTableColumn<StorageQuotaWarning, string>[] = [
    { key: "tenant", header: "Tenant", render: (row) => row.tenant_name },
    {
      key: "percent",
      header: "Storage used",
      align: "right",
      render: (row) => `${((row.storage_used_bytes / row.storage_quota_bytes) * 100).toFixed(0)}%`,
    },
    {
      key: "bytes",
      header: "Used / Quota",
      align: "right",
      render: (row) => `${(row.storage_used_bytes / 1024 ** 3).toFixed(1)} GB / ${(row.storage_quota_bytes / 1024 ** 3).toFixed(0)} GB`,
    },
  ];

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Operational Queues</h1>
          <p className="page__subtitle">
            Platform-wide, across every business. Age is computed live from each item's timestamp against a{" "}
            {DEFAULT_STALE_THRESHOLD_MS / 60_000}-minute staleness threshold.
          </p>
        </div>
      </div>

      {error ? <StatusBanner kind="error">{error}</StatusBanner> : null}

      <div className="stat-row">
        <div className="stat-tile">
          <p className="stat-tile__label">Sync conflicts</p>
          <p className="stat-tile__value">{data?.conflicts.length ?? (loading ? "…" : 0)}</p>
        </div>
        <div className="stat-tile">
          <p className="stat-tile__label">Conflicts aged &gt; 15m</p>
          <p className={`stat-tile__value ${countAgedBeyondThreshold(data?.conflicts ?? []) > 0 ? "stat-tile__value--danger" : ""}`}>
            {countAgedBeyondThreshold(data?.conflicts ?? [])}
          </p>
        </div>
        <div className="stat-tile">
          <p className="stat-tile__label">Pending reconciliations</p>
          <p className="stat-tile__value">{data?.reconciliations.length ?? (loading ? "…" : 0)}</p>
        </div>
        <div className="stat-tile">
          <p className="stat-tile__label">Reconciliations aged &gt; 15m</p>
          <p
            className={`stat-tile__value ${countAgedBeyondThreshold(data?.reconciliations ?? []) > 0 ? "stat-tile__value--danger" : ""}`}
          >
            {countAgedBeyondThreshold(data?.reconciliations ?? [])}
          </p>
        </div>
        <div className="stat-tile">
          <p className="stat-tile__label">
            Businesses near storage quota <MockBadge live={false} label="mock" title="Storage usage isn't tracked yet for this deployment — always simulated here, or empty when the real client is active." />
          </p>
          <p className={`stat-tile__value ${(data?.storageWarnings.length ?? 0) > 0 ? "stat-tile__value--warning" : ""}`}>
            {data?.storageWarnings.length ?? (loading ? "…" : 0)}
          </p>
        </div>
      </div>

      <div className="panel">
        <h2 className="panel__title">sync_conflicts queue</h2>
        <DataTable
          columns={conflictColumns}
          rows={agedConflicts}
          getRowId={(row) => row.item.id}
          emptyMessage={loading ? "Loading…" : "No sync conflicts outstanding."}
        />
      </div>

      <div className="panel">
        <h2 className="panel__title">Payment reconciliation — PENDING</h2>
        <DataTable
          columns={reconciliationColumns}
          rows={agedReconciliations}
          getRowId={(row) => row.item.id}
          emptyMessage={loading ? "Loading…" : "No pending reconciliations."}
        />
      </div>

      <div className="panel">
        <h2 className="panel__title">
          Tenants approaching storage quota (&ge; 80%){" "}
          <MockBadge live={false} label="not available (real mode)" title="Always empty when realPlatformApiClient is active — no plan/quota data exists server-side." />
        </h2>
        <DataTable
          columns={storageColumns}
          rows={data?.storageWarnings ?? []}
          getRowId={(row) => row.tenant_id}
          emptyMessage={loading ? "Loading…" : "No tenant is near its storage quota."}
        />
      </div>
    </div>
  );
}
